'use client'

import { useTranslations } from 'next-intl'

import { TRoomDetail, TSimpleReviewCount } from '@/entities/room'
import { cn, useMultiScrollState } from '@/shared/lib'
import { Separator } from '@/shared/ui'

import { Heading } from '../common'
import { ReviewDrawerTrigger } from './review-drawer'
import {
  MAX_REVIEW_DISPLAY_COUNT,
  ReviewList,
  ShowAllReviewsButton,
} from './review-list'
import { ReviewScore } from './review-score'
import { SimpleReview } from './simple-review'
import { TabValue } from '../../lib/tab'

type Props = Pick<TRoomDetail, 'reviewScore' | 'reviewList'> & {
  roomSimpleReview: TSimpleReviewCount[]
}

export const MobileReview = ({
  reviewScore,
  reviewList,
  roomSimpleReview,
}: Props) => {
  const t = useTranslations('room_detail')

  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const totalReviewCount = reviewList.length

  const hasSimpleReview = roomSimpleReview.length > 0
  const hasReviewList = totalReviewCount > 0

  const hasNoReview = !hasSimpleReview && !hasReviewList

  const isMoreThanMaxReviewDisplayCount =
    reviewList.length > MAX_REVIEW_DISPLAY_COUNT

  return (
    <section
      className="@container px-4 py-10"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.REVIEW, element)
        }
      }}
    >
      <Heading size="small" className={cn('mb-4', hasNoReview && 'mb-6')}>
        {t('review.title')}
      </Heading>
      {(() => {
        if (hasNoReview) {
          return (
            <p className="typo-body2 font-normal text-gray-900">
              {t('review.empty')}
            </p>
          )
        }

        if (hasSimpleReview && !hasReviewList) {
          return <SimpleReview simpleReviews={roomSimpleReview} />
        }

        if (!hasSimpleReview && hasReviewList) {
          return (
            <>
              <ReviewScore reviewScore={reviewScore} className="mb-6" />
              <ReviewList reviewList={reviewList} isMobile />
              {isMoreThanMaxReviewDisplayCount && (
                <ReviewDrawerTrigger
                  reviewList={reviewList}
                  reviewScore={reviewScore}
                  roomSimpleReview={roomSimpleReview}
                >
                  <ShowAllReviewsButton
                    totalReviewCount={totalReviewCount}
                    isMobile
                  />
                </ReviewDrawerTrigger>
              )}
            </>
          )
        }

        return (
          <>
            <ReviewScore reviewScore={reviewScore} className="mb-6" />
            <SimpleReview simpleReviews={roomSimpleReview} />
            <Separator color="gray" className="my-6" />
            <ReviewList reviewList={reviewList} isMobile />
            {isMoreThanMaxReviewDisplayCount && (
              <ReviewDrawerTrigger
                reviewList={reviewList}
                reviewScore={reviewScore}
                roomSimpleReview={roomSimpleReview}
              >
                <ShowAllReviewsButton
                  totalReviewCount={totalReviewCount}
                  isMobile
                />
              </ReviewDrawerTrigger>
            )}
          </>
        )
      })()}
    </section>
  )
}
